import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { curatorApi, usersApi } from '@/api/users';
import { Button } from '@/components/ui/Button';

/**
 * Кому адресовано правило: курсу, группе или одному ученику.
 *
 * ⚠️ СПИСКАМИ, А НЕ ИДЕНТИФИКАТОРОМ.
 *
 * До 04.09.2026 здесь стояло поле «Идентификатор группы, курса или ученика» —
 * то есть куратору предлагалось откуда-то взять UUID и вписать его руками.
 * Задать «в среду у такой-то группы пробный экзамен» было физически нечем, и
 * заказчик справедливо спросил, где вообще работать с расписанием.
 *
 * Списки берутся ИЗ СВОИХ: `/curator/groups`, `/curator/marathons`,
 * `/curator/students` отдают только то, что ведёт этот куратор. Администратор
 * видит через те же ручки свой набор — отдельной ветки «для админа» здесь нет
 * намеренно, иначе появилось бы два разных понимания «моих групп».
 */
export type AudienceKind = 'group' | 'marathon' | 'student' | 'all';

/**
 * ⚠️ ВИД АДРЕСАТА — ПАРАМЕТР ТИПА, а не общий набор на всех. Иначе «всем»
 * протекло бы в задания и расписание, где такого адресата не бывает, и сервер
 * отбил бы это уже в бою. Пусть ошибка всплывает при сборке.
 */
export type AudienceValue<K extends AudienceKind = 'group' | 'marathon' | 'student'> = {
  audience_type?: K;
  marathon_id?: string | null;
  group_id?: string | null;
  student_id?: string | null;
};

export function AudiencePicker<K extends AudienceKind = 'group' | 'marathon' | 'student'>({
  value,
  onChange,
  labelPrefix,
  types = ['group', 'marathon', 'student'] as unknown as readonly K[],
}: {
  value: AudienceValue<K>;
  onChange: (patch: AudienceValue<K>) => void;
  /** Раздел переводов: `curatorTasks` или `trainerSchedule`. */
  labelPrefix: string;
  /**
   * Какие виды адресата предлагать.
   *
   * ⚠️ Набор РАЗНЫЙ у разных сущностей. Задание можно дать одному ученику, а
   * разговорную комнату — нет: она про разговор нескольких. Зато комната бывает
   * открытой для всех, а задание «всем» не бывает.
   */
  types?: readonly K[];
}) {
  const { t } = useTranslation();

  const groups = useQuery({ queryKey: ['curator', 'groups'], queryFn: usersApi.myCuratorGroups });
  const marathons = useQuery({ queryKey: ['curator', 'marathons'], queryFn: curatorApi.listMyMarathons });
  const students = useQuery({ queryKey: ['curator', 'students'], queryFn: curatorApi.listStudents });

  const type = value.audience_type ?? types[0];

  // Что показывать в списке и куда писать выбор — по виду адресата.
  const options =
    type === 'marathon'
      ? (marathons.data?.items ?? []).map((m) => ({ id: m.marathon_id, label: m.title }))
      : type === 'student'
        ? (students.data ?? []).map((s) => ({ id: s.user_id, label: s.full_name || s.email || s.user_id }))
        : (groups.data?.items ?? []).map((g) => ({ id: g.id, label: g.name }));

  const current =
    type === 'marathon' ? value.marathon_id : type === 'student' ? value.student_id : value.group_id;

  const loading =
    type === 'marathon' ? marathons.isLoading : type === 'student' ? students.isLoading : groups.isLoading;

  const pick = (id: string) => {
    // ⚠️ Чужие поля обнуляются: правило с двумя адресатами сразу отобрал бы
    // сервер, а куратор увидел бы отказ без объяснения.
    if (type === 'marathon') onChange({ marathon_id: id || null, group_id: null, student_id: null });
    else if (type === 'student') onChange({ student_id: id || null, group_id: null, marathon_id: null });
    else onChange({ group_id: id || null, marathon_id: null, student_id: null });
  };

  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-on-surface">{t(`${labelPrefix}.whom`)}</p>
      <div className="flex flex-wrap gap-2">
        {types.map((a) => (
          <Button
            key={a}
            variant={type === a ? 'primary' : 'ghost'}
            onClick={() => onChange({ audience_type: a, marathon_id: null, group_id: null, student_id: null })}
          >
            {t(`${labelPrefix}.audience_${a}`)}
          </Button>
        ))}
      </div>

      {/* «Всем» выбирать не из чего — списка нет намеренно. */}
      {type !== 'all' && (
      <select
        className="mt-2 w-full rounded-xl border border-outline-variant bg-surface px-3 py-2 text-sm"
        value={current ?? ''}
        onChange={(e) => pick(e.target.value)}
      >
        <option value="">{loading ? '…' : t(`${labelPrefix}.whomPick`)}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      )}

      {/* Пустой список объясняет себя: иначе куратор решит, что страница сломана. */}
      {type !== 'all' && !loading && options.length === 0 && (
        <p className="mt-1 text-xs text-warning">{t(`${labelPrefix}.whomEmpty_${type}`)}</p>
      )}
    </div>
  );
}
